import { Box, Stack, Typography, useTheme } from "@mui/material";
import { type FC, Fragment, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE } from "../../../../theme/tokens";
import { goldAccent, heroAccent, heroGlassPill, heroInk, heroInkMuted, heroLine, heroSuccessInk, PBJ_RADIUS } from "../colors";
import { PREDICTION_MULTICOL_QUERY } from "../predictionLayout.consts";

type Props = {
	// True when the facility already meets its target star rating (calm confirmation copy).
	meetsTarget: boolean;
	// The (buffered) target star rating the whole story measures against.
	targetStars: number;
	// True when the Action Plan should be stated in the verdict: it lifts the score, or it cuts hours
	// (a cut-only or net-zero swap plan lifts nothing but is still the user's plan).
	planIsShown: boolean;
	// True when an Action Plan lifts the score beyond current — switches the narrative headline.
	hasActivePlan: boolean;
	// True when the active plan clears the buffered target (success vs "still short" verdict).
	planReachesTarget: boolean;
	// At target only: how many points the plan's cuts drop the department below its goal; null when
	// the plan keeps the goal (or there's no plan with cuts).
	planDropsBelowBy: number | null;
	// The plan line's prefix and the savings line credit "Suggested plan" only while the plan is the
	// optimizer's own; a plan the user built or changed reads "Your plan".
	planIsSuggested: boolean;
	// Points still needed to reach target with NO plan — the "suggest one" prompt.
	pointsGap: number;
	// Plan-projected points, rounded — the "with-plan" outcome in the verdict.
	withPlanPoints: number;
	// How far the plan still falls short of the rounded target (the "still N short" copy).
	withPlanGap: number;
	// The rounded target points — the verdict thresholds.
	roundedTarget: number;
	// The plan's weekly hours, split by direction (rounded magnitudes), for "+A hrs/wk · −C hrs/wk".
	planAddedHours: number;
	planCutHours: number;
	// The plan's NET PPD as a formatted magnitude ("$92"), and whether that net is savings.
	planPpdLabel: string;
	planIsNetSavings: boolean;
	// Quarter savings ("$12,400") when the facility is already at target and the plan saves money
	// while keeping it (decision H); null otherwise.
	planSavesLabel: string | null;
};

/**
 * The hero's centerpiece — the "gap story": an eyebrow, a large narrative headline that reads
 * the whole decision, and a glass verdict callout (plan cost + outcome, or a calm confirmation,
 * or a prompt to suggest a plan). The now→projected→target meter that used to live here now
 * runs full-width beneath the hero (HeroProgressMeter), so this block carries the type weight.
 * Pure presentation of the passed scalars/prediction.
 */
export const HeroGapStory: FC<Props> = ({
	meetsTarget,
	targetStars,
	planIsShown,
	hasActivePlan,
	planReachesTarget,
	planDropsBelowBy,
	planIsSuggested,
	pointsGap,
	withPlanPoints,
	withPlanGap,
	roundedTarget,
	planAddedHours,
	planCutHours,
	planPpdLabel,
	planIsNetSavings,
	planSavesLabel,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const gold = goldAccent(theme);
	// A success ink readable on the dark hero stage — a lightened success green (the raw
	// success.main is too dark on the deep-primary gradient). Shared token so the plan-summary
	// bookend renders its "target reached" verdict in the exact same on-dark success ink.
	const successInk = heroSuccessInk(theme);

	// "+A hrs/wk · −C hrs/wk · $X PPD" — each direction only when the plan moves it (an add-only
	// plan reads exactly as before), then the net PPD as spend or savings.
	const planSegments: { key: string; text: string }[] = [
		...(planAddedHours > 0 || planCutHours === 0 ? [{ key: "added", text: t("pbj_model.hero.verdict.plan_hours", { hours: planAddedHours }) }] : []),
		...(planCutHours > 0 ? [{ key: "cut", text: t("pbj_model.hero.verdict.plan_hours_cut", { hours: planCutHours }) }] : []),
		{
			key: "ppd",
			text: planIsNetSavings
				? t("pbj_model.hero.verdict.plan_ppd_savings", { savings: planPpdLabel })
				: t("pbj_model.hero.verdict.plan_ppd", { cost: planPpdLabel }),
		},
	];
	// "→ N pts" followed by what that projection means against the goal.
	const projectedOutcome = (verdict: ReactNode, dataTestId: string): ReactNode => (
		<Stack
			direction="row"
			spacing={1}
			sx={{
				alignItems: "baseline",
				flexWrap: "wrap",
				rowGap: 0.25,
				justifyContent: "center",
				[PREDICTION_MULTICOL_QUERY]: { justifyContent: "flex-start" },
			}}
			data-testid={dataTestId}
		>
			<Typography component="span" sx={{ fontSize: FONT_SIZE.TITLE_MD, fontWeight: 700, color: heroAccent(theme), fontVariantNumeric: "tabular-nums" }}>
				→ {t("pbj_model.hero.verdict.projected_pts", { points: withPlanPoints })}
			</Typography>
			{verdict}
		</Stack>
	);
	const planSummaryLine = (withPrefix: boolean): ReactNode => (
		<Typography
			sx={{ fontSize: FONT_SIZE.EMPHASIS, color: heroInkMuted(), mb: 0.5, textAlign: "center", [PREDICTION_MULTICOL_QUERY]: { textAlign: "left" } }}
			data-testid="pbj-model-hero-verdict-plan"
		>
			{withPrefix && (
				<>
					<Box component="span" sx={{ fontWeight: 500 }}>
						{t(planIsSuggested ? "pbj_model.hero.verdict.plan_prefix" : "pbj_model.hero.verdict.plan_prefix_own")}
					</Box>{" "}
				</>
			)}
			{planSegments.map((segment, index) => (
				<Fragment key={segment.key}>
					{index > 0 && " · "}
					<Box
						component="span"
						sx={{ color: segment.key === "ppd" && planIsNetSavings ? successInk : heroInk(), fontWeight: 700 }}
						data-testid={`pbj-model-hero-verdict-plan-${segment.key}`}
					>
						{segment.text}
					</Box>
				</Fragment>
			))}
		</Typography>
	);

	return (
		<Box
			sx={{
				minWidth: 0,
				width: "100%",
				color: heroInk(),
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				[PREDICTION_MULTICOL_QUERY]: { display: "block" },
			}}
		>
			<Typography
				sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: heroAccent(theme), mb: 0.75 }}
			>
				{t("pbj_model.hero.rating_eyebrow")}
			</Typography>

			{/* The narrative headline — reads the whole decision in one line. Enlarged so it's the
			    hero's centerpiece, not thin text in a wide gap. N is the TARGET stars. */}
			<Typography
				sx={{ fontSize: { xs: "1.5rem", md: "1.75rem" }, fontWeight: 700, letterSpacing: "-0.015em", lineHeight: 1.2, color: heroInk(), mb: 1.25 }}
				data-testid="pbj-model-hero-narrative"
			>
				{meetsTarget
					? t("pbj_model.hero.at_target_headline", { count: targetStars })
					: hasActivePlan
						? planReachesTarget
							? t("pbj_model.hero.plan_reaches_headline", { stars: targetStars })
							: t("pbj_model.hero.plan_closer_headline")
						: t("pbj_model.hero.on_your_way", { count: targetStars })}
			</Typography>

			{/* VERDICT — the closed-loop callout. When a plan is active it states the plan's cost
			    ("+{hours} hrs/wk · ${ppd} PPD") and its outcome ("→ {withPlan} pts — still {gap}
			    short of {stars}★ ({target})" / "reaches your {stars}★ target ✓"). When on target
			    already, a calm confirmation. With no plan, a prompt to build one. */}
			<Box
				sx={{
					width: "100%",
					maxWidth: { xs: 460, md: "none" },
					px: 2,
					py: 1.25,
					borderRadius: PBJ_RADIUS.INSET,
					bgcolor: heroGlassPill(),
					border: `1px solid ${heroLine()}`,
				}}
				data-testid="pbj-model-hero-verdict"
			>
				{meetsTarget && planSavesLabel !== null ? (
					<>
						{planSummaryLine(false)}
						<Typography
							sx={{
								fontSize: FONT_SIZE.TITLE_SM,
								fontWeight: 700,
								color: successInk,
								textAlign: "center",
								[PREDICTION_MULTICOL_QUERY]: { textAlign: "left" },
							}}
							data-testid="pbj-model-hero-verdict-plan-saves"
						>
							{t(planIsSuggested ? "pbj_model.hero.verdict.plan_saves" : "pbj_model.hero.verdict.plan_saves_own", {
								savings: planSavesLabel,
								stars: targetStars,
								points: roundedTarget,
							})}
						</Typography>
					</>
				) : meetsTarget && planDropsBelowBy !== null ? (
					// At target, but the plan's cuts would take the department below its goal — the plan is
					// stated with a warning-toned verdict instead of hiding behind "You've cleared your goal".
					<>
						{planSummaryLine(false)}
						{projectedOutcome(
							<Typography component="span" sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 600, color: gold }} data-testid="pbj-model-hero-verdict-plan-drops">
								{t("pbj_model.hero.verdict.plan_drops_below", { count: planDropsBelowBy, stars: targetStars, points: roundedTarget })}
							</Typography>,
							"pbj-model-hero-verdict-outcome",
						)}
					</>
				) : meetsTarget ? (
					<Stack
						direction="row"
						spacing={1}
						sx={{
							alignItems: "baseline",
							flexWrap: "wrap",
							rowGap: 0.5,
							justifyContent: "center",
							[PREDICTION_MULTICOL_QUERY]: { justifyContent: "flex-start" },
						}}
					>
						<Typography component="span" sx={{ fontSize: FONT_SIZE.TITLE_MD, fontWeight: 700, color: successInk }}>
							{t("pbj_model.hero.verdict.on_track_reached", { stars: targetStars, points: roundedTarget })}
						</Typography>
					</Stack>
				) : planIsShown ? (
					<>
						{planSummaryLine(true)}
						{projectedOutcome(
							planReachesTarget ? (
								<Typography component="span" sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 600, color: successInk }}>
									{t("pbj_model.hero.verdict.reaches", { stars: targetStars, points: roundedTarget })}
								</Typography>
							) : (
								<Typography component="span" sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 600, color: gold }}>
									{t("pbj_model.hero.verdict.short", { gap: withPlanGap, stars: targetStars, points: roundedTarget })}
								</Typography>
							),
							"pbj-model-hero-verdict-outcome",
						)}
					</>
				) : (
					<Stack
						direction="row"
						spacing={1}
						sx={{ alignItems: "center", justifyContent: "center", [PREDICTION_MULTICOL_QUERY]: { justifyContent: "flex-start" } }}
					>
						<Typography component="span" sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 600, color: heroInk() }}>
							{t("pbj_model.hero.verdict.no_plan_prompt", { gap: pointsGap, stars: targetStars })}
						</Typography>
					</Stack>
				)}
			</Box>
		</Box>
	);
};
